<template>
    <cr-confirmation-dialog :title="title" :hide-title="hideTitle" :cancel="false" :confirm="false" persistent>
        <div v-if="loading" class="submission-dialog-loading-wrapper">
            <cr-loading :message="t('SUBMISSION_DIALOG_LOADING_MESSAGE')" data-testing="submission-dialog-loading"/>
        </div>
        <template v-else>
            <div class="cr-mb4">
                <cr-row v-if="result?.correct && levelCompleted && !exhausted">
                    <p data-testing="submission-dialog-level-completed"><strong>{{ t('SUBMISSION_DIALOG_LEVEL_COMPLETED', {level: level.namePrefix}) }}</strong></p>
                </cr-row>
                <cr-row v-if="exhausted">
                    <p data-testing="submission-dialog-exhausted-message">{{ t('SUBMISSION_DIALOG_EXHAUSTED_MESSAGE') }}</p>
                </cr-row>
                <cr-row v-if="starImageSrc">
                    <cr-image :src="starImageSrc" :alt="t('SUBMISSION_DIALOG_STAR_IMAGE_ALT', {n: result?.points, total: 3})" max-height="3rem" class="cr-mb2" data-testing="submission-dialog-star-image"/>
                </cr-row>
                <cr-row>
                    <cr-image :src="corgiImageSrc" class="cr-pl5" alt="" max-height="6rem" data-testing="submission-dialog-corgi-image"/>
                </cr-row>
            </div>
            <cr-row v-if="result?.explanation">
                <cr-type-writer data-testing="submission-dialog-explanation" :text="result.explanation" reserve-space/>
            </cr-row>
        </template>
        <template #footerRight v-if="!loading">
            <template v-if="exhausted">
                <cr-button :to="{ name: Route.LevelSelect.name }" background-color="cr-confirmation-dialog-confirm-background-color" text-color="cr-confirmation-dialog-confirm-text-color" data-testing="submission-dialog-back-to-menu">
                    {{ t('SUBMISSION_DIALOG_BACK_TO_MENU') }}
                </cr-button>
            </template>
            <template v-else-if="!result?.correct">
                <cr-button v-if="!atMaxAttempts" @click="onRetryClicked" background-color="cr-confirmation-dialog-confirm-background-color" text-color="cr-confirmation-dialog-confirm-text-color" data-testing="submission-dialog-incorrect-retry">
                    {{ t('SUBMISSION_DIALOG_RETRY') }}
                </cr-button>
                <cr-button @click="onNextClicked" :text="!atMaxAttempts" :background-color="atMaxAttempts ? 'cr-confirmation-dialog-confirm-background-color' : undefined" :text-color="atMaxAttempts ? 'cr-confirmation-dialog-confirm-text-color' : undefined" data-testing="submission-dialog-incorrect-next">
                    {{ t('SUBMISSION_DIALOG_NEXT') }}
                </cr-button>
            </template>
            <template v-else-if="levelCompleted">
                <cr-button @click="onNextClicked" background-color="cr-confirmation-dialog-confirm-background-color" text-color="cr-confirmation-dialog-confirm-text-color" data-testing="submission-dialog-completed-continue">
                    {{ t('SUBMISSION_DIALOG_CONTINUE_LEVEL', {level: level.namePrefix}) }}
                </cr-button>
                <cr-button :to="{ name: Route.LevelSelect.name }" text data-testing="submission-dialog-completed-back-to-menu">
                    {{ t('SUBMISSION_DIALOG_BACK_TO_MENU') }}
                </cr-button>
            </template>
            <template v-else>
                <cr-button @click="onNextClicked" background-color="cr-confirmation-dialog-confirm-background-color" text-color="cr-confirmation-dialog-confirm-text-color" data-testing="submission-dialog-correct-next">
                    {{ t('SUBMISSION_DIALOG_NEXT') }}
                </cr-button>
            </template>
        </template>
    </cr-confirmation-dialog>
</template>
<style scoped>
.submission-dialog-loading-wrapper
{
    display: flex;
    justify-content: center;
}
</style>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import Route from '../../routers/route';
import ILevel from '../../interfaces/iLevel';
import { computed, watch } from 'vue';
import { ISubmissionResult } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { useAnnouncementStore } from '@cyber-range/cyber-range-lib-ui';
const { t } = useI18n();

const props = defineProps<
{
    level: ILevel
    result?: ISubmissionResult
    levelCompleted?: boolean
    exhausted?: boolean
    loading?: boolean
    atMaxAttempts?: boolean
}>();

const emit = defineEmits<{
    (name: 'next'): void,
    (name: 'retry'): void,
}>();

const onRetryClicked = () => emit('retry');
const onNextClicked = () => emit('next');


const starImageSrc = computed(() => {
    const points = props.result?.points;

    if (!points || !props.result?.correct)
    {
        return undefined
    }

    return `/${points}-stars-earned.svg`;
})

const corgiImageSrc = computed(() => props.exhausted || props.result?.correct ? props.level.happyImageSrc : props.level.sadImageSrc);

watch(() => props.loading, () =>
{
    if (props.loading)
    {
        useAnnouncementStore().announce(t('SUBMISSION_DIALOG_LOAD_START_ANNOUNCEMENT'))
    }
    else
    {
        useAnnouncementStore().announce(t('SUBMISSION_DIALOG_LOAD_DONE_ANNOUNCEMENT', {title: title.value}))
    }
})

const hideTitle = computed(() => props.loading);

const title = computed(() => {
    if (props.loading)
    {
        return "";
    }
    if (props.exhausted)
    {
        return t('SUBMISSION_DIALOG_TITLE_EXHAUSTED', {level: props.level.namePrefix});
    }

    const points = props.result?.points;

    if (!props.result?.correct)
    {
        return t('SUBMISSION_DIALOG_TITLE_INCORRECT');
    }

    if (points && props.levelCompleted)
    {
        return t('SUBMISSION_DIALOG_TITLE_STARS_LEVEL_COMPLETE', {n: points, level: props.level.namePrefix}, points)
    }

    if (points)
    {
        return t('SUBMISSION_DIALOG_TITLE_STARS', points);
    }

    return t('SUBMISSION_DIALOG_TITLE_CORRECT');
});
</script>
